/**
 * OmniClinicalBridge PWA Service Worker Registration
 * OmniAlera Technologies Holdings Inc.
 * 
 * Handles service worker lifecycle, updates, and offline notifications
 */

interface SWConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

let swRegistration: ServiceWorkerRegistration | null = null;

export function registerSW(config?: SWConfig): void {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        
        swRegistration = registration;
        
        console.log('[PWA] Service Worker registered successfully:', registration.scope);
        
        // Handle updates
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // New content is available; please refresh
                  console.log('[PWA] New content available; please refresh.');
                  config?.onUpdate?.(registration);
                  
                  // Dispatch custom event for UI notification
                  window.dispatchEvent(new CustomEvent('swUpdate', { detail: registration }));
                } else {
                  // Content is cached for offline use
                  console.log('[PWA] Content cached for offline use.');
                  config?.onSuccess?.(registration);
                  
                  // Dispatch custom event
                  window.dispatchEvent(new CustomEvent('swReady'));
                }
              }
            };
          }
        };
        
        // Periodic update check (every hour)
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
        
      } catch (error) {
        console.error('[PWA] Service Worker registration failed:', error);
      }
    });
    
    // Handle online/offline events
    window.addEventListener('online', () => {
      console.log('[PWA] Back online');
      config?.onOnline?.();
      window.dispatchEvent(new CustomEvent('networkOnline'));
    });
    
    window.addEventListener('offline', () => {
      console.log('[PWA] Gone offline');
      config?.onOffline?.();
      window.dispatchEvent(new CustomEvent('networkOffline'));
    });
  } else {
    console.warn('[PWA] Service Workers not supported in this browser');
  }
}

// Skip waiting and activate new service worker
export function skipWaiting(): void {
  if (swRegistration?.waiting) {
    swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    window.location.reload();
  }
}

// Check if app is installed
export function isAppInstalled(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
}

// Get current SW registration
export function getRegistration(): ServiceWorkerRegistration | null {
  return swRegistration;
}

// Check if app can be installed
export function canInstall(): boolean {
  return 'BeforeInstallPromptEvent' in window;
}
